<template>
    <div class="condition-builder">
        <!-- Action & Logic selector -->
        <div class="condition-builder__selectors">
            <div>
                <Field
                    v-model="localConditions.action"
                    :field="{
                        name: lang('fields::fields.field-editor.condition_action'),
                        slug: 'action',
                        type: 'select',
                        adminComponent: 'AdminSelect',
                        options: actionOptions,
                    }"
                    label-position="top"
                    @change="emitUpdate"
                />
            </div>

            <div>
                <Field
                    v-model="localConditions.logic"
                    :field="{
                        name: lang('fields::fields.field-editor.condition_logic'),
                        slug: 'logic',
                        type: 'select',
                        adminComponent: 'AdminSelect',
                        options: logicOptions,
                    }"
                    label-position="top"
                    @change="emitUpdate"
                />
            </div>
        </div>

        <!-- Rules list -->
        <div class="condition-builder__rules">
            <div v-for="(rule, index) in localConditions.rules" :key="index" class="condition-builder__rule">
                <div class="condition-builder__rule-field">
                    <AdminSelect
                        v-model="rule.field"
                        :options="availableFieldOptions"
                        :placeholder="lang('fields::fields.field-editor.select_field')"
                        @update:model-value="emitUpdate"
                    />
                </div>

                <div class="condition-builder__rule-comparison">
                    <AdminSelect
                        v-model="rule.comparison"
                        :options="comparisonOptions"
                        @update:model-value="emitUpdate"
                    />
                </div>

                <div class="condition-builder__rule-value">
                    <AdminText
                        v-model="rule.value"
                        :placeholder="lang('fields::fields.field-editor.value')"
                        @update:model-value="emitUpdate"
                    />
                </div>

                <button type="button" class="condition-builder__rule-remove" @click="removeRule(index)">
                    <icon icon="hero/outline/x-mark" class="condition-builder__rule-remove-icon" />
                </button>
            </div>
        </div>

        <!-- Add rule button -->
        <div class="condition-builder__add">
            <Button type="button" size="sm" variant="secondary" @click="addRule">
                <icon icon="hero/outline/plus" class="w-4 h-4 mr-1" />
                {{ lang('fields::fields.field-editor.add_condition') }}
            </Button>
        </div>

        <!-- Preview -->
        <div v-if="localConditions.rules && localConditions.rules.length > 0" class="condition-builder__preview">
            <label class="condition-builder__preview-label">
                {{ lang('fields::fields.field-editor.condition_preview') }}
            </label>
            <div class="condition-builder__preview-text">
                <span class="condition-builder__preview-action">
                    {{
                        localConditions.action === 'show'
                            ? lang('fields::fields.field-editor.show_field')
                            : lang('fields::fields.field-editor.hide_field')
                    }}
                </span>
                {{ lang('fields::fields.field-editor.when') }}
                <span v-for="(rule, index) in localConditions.rules" :key="index">
                    <span class="condition-builder__preview-field">{{ getFieldName(rule.field) }}</span>
                    {{ getComparisonName(rule.comparison) }}
                    <span class="condition-builder__preview-value">"{{ rule.value }}"</span>
                    <span v-if="index < localConditions.rules.length - 1" class="condition-builder__preview-connector">
                        {{
                            localConditions.logic === 'all'
                                ? lang('fields::fields.field-editor.and')
                                : lang('fields::fields.field-editor.or')
                        }}
                    </span>
                </span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { lang } from '@/lang/composable';

interface ConditionRule {
    field: string;
    comparison: string;
    value: string;
}

interface ConditionsData {
    action: string;
    logic: string;
    rules: ConditionRule[];
}

interface AvailableField {
    slug: string;
    name: string;
}

interface Props {
    conditions: Partial<ConditionsData>;
    availableFields: AvailableField[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (event: 'update:conditions', conditions: ConditionsData): void;
}>();

const localConditions = ref<ConditionsData>({
    action: props.conditions.action ?? 'show',
    logic: props.conditions.logic ?? 'all',
    rules: props.conditions.rules ?? [],
});

watch(
    () => props.conditions,
    (newConditions) => {
        localConditions.value = {
            action: newConditions.action ?? 'show',
            logic: newConditions.logic ?? 'all',
            rules: newConditions.rules ?? [],
        };
    },
    { deep: true },
);

const actionOptions = computed(() => [
    { id: 'show', name: lang('fields::fields.field-editor.action_show') },
    { id: 'hide', name: lang('fields::fields.field-editor.action_hide') },
]);

const logicOptions = computed(() => [
    { id: 'all', name: lang('fields::fields.field-editor.logic_all') },
    { id: 'any', name: lang('fields::fields.field-editor.logic_any') },
]);

const comparisonOptions = computed(() => [
    { id: '==', name: lang('fields::fields.comparison.==') },
    { id: '!=', name: lang('fields::fields.comparison.!=') },
    { id: '>', name: lang('fields::fields.comparison.>') },
    { id: '>=', name: lang('fields::fields.comparison.>=') },
    { id: '<', name: lang('fields::fields.comparison.<') },
    { id: '<=', name: lang('fields::fields.comparison.<=') },
    { id: 'in', name: lang('fields::fields.comparison.in') },
    { id: 'notin', name: lang('fields::fields.comparison.not_in') },
    { id: 'empty', name: lang('fields::fields.field-editor.comparison_empty') },
    { id: 'not_empty', name: lang('fields::fields.field-editor.comparison_not_empty') },
]);

const availableFieldOptions = computed(() =>
    props.availableFields.map((field) => ({
        id: field.slug,
        name: field.name || field.slug,
    })),
);

const getFieldName = (slug: string) => {
    const field = props.availableFields.find((f) => f.slug === slug);
    return field?.name || slug;
};

const getComparisonName = (comparison: string) => {
    const option = comparisonOptions.value.find((o) => o.id === comparison);
    return option?.name || comparison;
};

const addRule = () => {
    localConditions.value.rules.push({
        field: '',
        comparison: '==',
        value: '',
    });
    emitUpdate();
};

const removeRule = (index: number) => {
    localConditions.value.rules.splice(index, 1);
    emitUpdate();
};

const emitUpdate = () => {
    emit('update:conditions', { ...localConditions.value });
};
</script>
